import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { searchPublicWorkspaces } from "../../api/workspaceMarketplace";
import MarketplaceShell from "./MarketplaceShell";
import {
  formatWorkspaceMoney,
  getWorkspaceCheckoutPath,
  getWorkspaceDisplayName,
  getWorkspaceListingPath,
  getWorkspaceLocation,
  titleCaseLabel,
} from "../../utils/workspacePresentation";
import { resolveAssetUrl } from "../../utils/assetUrl";

const fieldClass =
  "h-11 rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10";

const bookingModeHelp = [
  {
    value: "internal",
    label: "Internal",
    description:
      "Managed inside the salon workflow and usually not posted for public rental requests.",
  },
  {
    value: "request",
    label: "Request",
    description:
      "Send a rental request and the workspace provider reviews it before confirming.",
  },
  {
    value: "instant",
    label: "Instant",
    description: "Reserve the workspace right away when it is available.",
  },
  {
    value: "invite_only",
    label: "Invite only",
    description:
      "The listing is limited to private access, referrals, or salon-approved professionals.",
  },
];

const searchHighlights = [
  "Salon owners can turn unused space into organized rental income.",
  "Independent beauty professionals can compare amenities, location, and rental terms.",
  "Ellyanna Boothshare keeps workspace discovery inside the Ellyanna product family.",
];

const searchShowcasePhotos = [
  {
    src: "/marketplace-placeholders/loc02.jpeg",
    alt: "Bright salon reception and pedicure area available for workspace rental.",
    label: "Workspace provider ready",
    featured: true,
  },
  {
    src: "/salon-desk2-2.jpg",
    alt: "Modern manicure stations prepared for professional workspace sharing.",
    label: "Nail stations",
  },
  {
    src: "/marketplace-placeholders/loc01.jpeg",
    alt: "Clean manicure area showing a professional salon environment.",
    label: "Professional setup",
  },
  {
    src: "/marketplace-placeholders/loc03.jpeg",
    alt: "Pedicure area inside a salon for independent beauty professionals.",
    label: "Client-ready space",
  },
];

function readFilters(params) {
  return {
    q: params.get("q") || "",
    location: params.get("location") || "",
    workspace_type: params.get("workspace_type") || "",
    booking_mode: params.get("booking_mode") || "",
    min_price: params.get("min_price") || "",
    max_price: params.get("max_price") || "",
    featured_only: params.get("featured_only") === "true",
  };
}

function filtersAreEqual(left, right) {
  return (
    left.q === right.q &&
    left.location === right.location &&
    left.workspace_type === right.workspace_type &&
    left.booking_mode === right.booking_mode &&
    left.min_price === right.min_price &&
    left.max_price === right.max_price &&
    left.featured_only === right.featured_only
  );
}

function buildFilterParams(filters) {
  const next = new URLSearchParams();
  if (filters.q) next.set("q", filters.q);
  if (filters.location) next.set("location", filters.location);
  if (filters.workspace_type) next.set("workspace_type", filters.workspace_type);
  if (filters.booking_mode) next.set("booking_mode", filters.booking_mode);
  if (filters.min_price) next.set("min_price", filters.min_price);
  if (filters.max_price) next.set("max_price", filters.max_price);
  if (filters.featured_only) next.set("featured_only", "true");
  return next;
}

function getWorkspaceCardPhoto(workspace, index) {
  if (Array.isArray(workspace?.photos) && workspace.photos.length) {
    return resolveAssetUrl(workspace.photos[0]);
  }
  return searchShowcasePhotos[index % searchShowcasePhotos.length]?.src || "";
}

function getWorkspaceCardPhotoAlt(workspace) {
  return `${getWorkspaceDisplayName(workspace)} workspace preview`;
}

export default function MarketplaceSearchPage() {
  const [params, setParams] = useSearchParams();
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const paramSignature = params.toString();
  const filters = useMemo(
    () => readFilters(new URLSearchParams(paramSignature)),
    [paramSignature]
  );
  const [draftFilters, setDraftFilters] = useState(filters);

  useEffect(() => {
    setDraftFilters((current) =>
      filtersAreEqual(current, filters) ? current : filters
    );
  }, [filters]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    searchPublicWorkspaces(
      Object.fromEntries(
        Object.entries(filters).filter(([, value]) =>
          typeof value === "boolean" ? value : String(value || "").trim()
        )
      )
    )
      .then((rows) => {
        if (active) setWorkspaces(Array.isArray(rows) ? rows : []);
      })
      .catch((err) => {
        if (active) {
          setError(err.message || "Unable to load workspace search.");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [filters]);

  const visibleWorkspaces = useMemo(() => {
    return workspaces.filter((workspace) => {
      if (
        filters.booking_mode &&
        String(workspace.booking_mode || "").toLowerCase() !== filters.booking_mode
      ) {
        return false;
      }
      return true;
    });
  }, [filters.booking_mode, workspaces]);

  const resultSummary = useMemo(() => {
    const average = visibleWorkspaces.length
      ? visibleWorkspaces.reduce(
          (sum, workspace) => sum + Number(workspace.price_per_hour || 0),
          0
        ) / visibleWorkspaces.length
      : 0;
    return {
      count: visibleWorkspaces.length,
      featured: visibleWorkspaces.filter((workspace) => workspace.is_featured)
        .length,
      average,
    };
  }, [visibleWorkspaces]);

  const activeBookingModeHelp =
    bookingModeHelp.find((item) => item.value === draftFilters.booking_mode) || null;

  const updateFilter = (key, value) => {
    setDraftFilters((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const hasPendingFilterChanges = !filtersAreEqual(draftFilters, filters);

  const applyFilters = () => {
    const next = buildFilterParams(draftFilters);
    setParams(next, { replace: true });
  };

  const clearFilters = () => {
    const emptyFilters = {
      q: "",
      location: "",
      workspace_type: "",
      booking_mode: "",
      min_price: "",
      max_price: "",
      featured_only: false,
    };
    setDraftFilters(emptyFilters);
    setParams(new URLSearchParams(), { replace: true });
  };

  return (
    <MarketplaceShell>
      <section className="rounded-[2rem] border border-slate-200 bg-white shadow-sm">
        <div className="grid gap-6 border-b border-slate-200 px-6 py-6 lg:grid-cols-[1.1fr,0.9fr]">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-500">
              Workspace Search
            </p>
            <h1 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900">
              Explore professional workspace opportunities on Ellyanna Boothshare.
            </h1>
            <p className="mt-3 max-w-2xl text-sm text-slate-600 md:text-base">
              Salon owners use Ellyanna Boothshare to turn unused stations, rooms, and chairs into organized rental income. Independent beauty professionals can search opportunities with clearer terms, amenities, and professional expectations.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {searchHighlights.map((item) => (
                <span
                  key={item}
                  className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
          <div className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-slate-50">
            <div className="grid gap-3 p-3 md:grid-cols-[1.18fr,0.82fr]">
              {searchShowcasePhotos
                .filter((photo) => photo.featured)
                .map((photo) => (
                  <div
                    key={photo.src}
                    className="group relative min-h-[230px] overflow-hidden rounded-[1.5rem] bg-slate-200"
                  >
                    <img
                      src={photo.src}
                      alt={photo.alt}
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-slate-900/20 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-rose-100">
                        {photo.label}
                      </div>
                      <div className="mt-2 max-w-xs text-lg font-semibold tracking-tight">
                        Explore polished workspaces from salon owners.
                      </div>
                    </div>
                  </div>
                ))}
              <div className="grid gap-3">
                {searchShowcasePhotos
                  .filter((photo) => !photo.featured)
                  .slice(0, 2)
                  .map((photo) => (
                    <div
                      key={photo.src}
                      className="group relative min-h-[108px] overflow-hidden rounded-[1.3rem] bg-slate-200"
                    >
                      <img
                        src={photo.src}
                        alt={photo.alt}
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-900/20 to-transparent" />
                      <div className="absolute inset-x-0 bottom-0 p-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-rose-100">
                        {photo.label}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
            <div className="grid gap-4 border-t border-slate-200 bg-white px-5 py-4 md:grid-cols-3">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Workspaces
                </div>
                <div className="mt-2 text-3xl font-semibold text-slate-900">
                  {resultSummary.count}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Featured
                </div>
                <div className="mt-2 text-3xl font-semibold text-slate-900">
                  {resultSummary.featured}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Avg. Rent
                </div>
                <div className="mt-2 text-3xl font-semibold text-slate-900">
                  {formatWorkspaceMoney(resultSummary.average)}
                </div>
              </div>
            </div>
          </div>
        </div>

        <form
          className="border-b border-slate-200 px-6 py-5"
          onSubmit={(event) => {
            event.preventDefault();
            applyFilters();
          }}
        >
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <input
              className={fieldClass}
              placeholder="Search workspace or salon"
              value={draftFilters.q}
              onChange={(event) => updateFilter("q", event.target.value)}
            />
            <input
              className={fieldClass}
              placeholder="City or location"
              value={draftFilters.location}
              onChange={(event) => updateFilter("location", event.target.value)}
            />
            <select
              className={fieldClass}
              value={draftFilters.workspace_type}
              onChange={(event) => updateFilter("workspace_type", event.target.value)}
            >
              <option value="">All workspace types</option>
              <option value="chair">Chair</option>
              <option value="suite">Suite</option>
              <option value="room">Room</option>
              <option value="station">Station</option>
              <option value="open_floor">Open floor</option>
            </select>
            <select
              className={fieldClass}
              value={draftFilters.booking_mode}
              onChange={(event) => updateFilter("booking_mode", event.target.value)}
            >
              <option value="">All rental request modes</option>
              <option value="internal">Internal - salon-managed</option>
              <option value="request">Request - approval needed</option>
              <option value="instant">Instant - request now</option>
              <option value="invite_only">Invite only - private access</option>
            </select>
            <input
              className={fieldClass}
              type="number"
              min="0"
              placeholder="Min hourly"
              value={draftFilters.min_price}
              onChange={(event) => updateFilter("min_price", event.target.value)}
            />
            <input
              className={fieldClass}
              type="number"
              min="0"
              placeholder="Max hourly"
              value={draftFilters.max_price}
              onChange={(event) => updateFilter("max_price", event.target.value)}
            />
            <label className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                checked={draftFilters.featured_only}
                onChange={(event) =>
                  updateFilter("featured_only", event.target.checked)
                }
              />
              Featured only
            </label>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <div className="space-y-2">
              <p className="text-sm text-slate-500">
                Search stays local until you press{" "}
                <span className="font-semibold text-slate-700">Apply Filters</span>.
              </p>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                <div className="font-semibold text-slate-900">Rental request mode guide</div>
                <p className="mt-1">
                  {activeBookingModeHelp
                    ? `${activeBookingModeHelp.label}: ${activeBookingModeHelp.description}`
                    : "Use Request when a workspace provider reviews each rental request, Instant for ready-to-request listings, or Invite only for private-access opportunities."}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={clearFilters}
                className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
              >
                Clear Filters
              </button>
              <button
                type="submit"
                className="rounded-full bg-slate-900 px-5 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
                disabled={!hasPendingFilterChanges}
              >
                {loading && !hasPendingFilterChanges ? "Searching..." : "Apply Filters"}
              </button>
            </div>
          </div>
        </form>

        <div className="px-6 py-6">
          {loading ? <p className="text-slate-600">Searching workspaces...</p> : null}
          {error ? (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {error}
            </div>
          ) : null}
          {!loading && !error && visibleWorkspaces.length === 0 ? (
            <div className="overflow-hidden rounded-[1.75rem] border border-dashed border-slate-300 bg-slate-50">
              <div className="grid gap-0 lg:grid-cols-[1.05fr,0.95fr]">
                <div className="relative min-h-[240px] overflow-hidden bg-slate-200">
                  <img
                    src="/marketplace-placeholders/loc03.jpeg"
                    alt="Pedicure area inside a salon available for future Ellyanna Boothshare listings."
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-900/20 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-rose-100">
                      More workspace providers coming
                    </div>
                    <div className="mt-2 max-w-xs text-xl font-semibold tracking-tight">
                      Check nearby cities or broaden your workspace filters.
                    </div>
                  </div>
                </div>
                <div className="flex items-center px-6 py-10 text-center text-slate-600 lg:text-left">
                  <div>
                    <div className="text-lg font-semibold tracking-tight text-slate-900">
                      No workspace opportunities matched your search yet.
                    </div>
                    <p className="mt-3 text-sm leading-7 md:text-base">
                      Try another city, adjust your filters, or check back as more salon owners publish space on Ellyanna Boothshare.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <div className="grid gap-4 xl:grid-cols-2">
            {visibleWorkspaces.map((workspace, index) => (
              <article
                key={workspace.id}
                className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-slate-50"
              >
                <div className="grid gap-0 md:grid-cols-[1fr,0.9fr]">
                  <div className="relative min-h-[260px] overflow-hidden bg-slate-200 text-white">
                    <img
                      src={getWorkspaceCardPhoto(workspace, index)}
                      alt={getWorkspaceCardPhotoAlt(workspace)}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-900/35 to-slate-900/10" />
                    <div className="relative flex h-full flex-col justify-end p-6">
                      <div className="flex items-center justify-between gap-3">
                        <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-rose-100">
                          {titleCaseLabel(workspace.workspace_type, "Workspace")}
                        </span>
                        <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-sm font-semibold">
                          {formatWorkspaceMoney(workspace.price_per_hour)}/hr
                        </span>
                      </div>
                      <h2 className="mt-5 text-2xl font-semibold tracking-tight">
                        {getWorkspaceDisplayName(workspace)}
                      </h2>
                      <p className="mt-2 text-sm text-slate-200">
                        {workspace.salon?.name || "Ellyanna salon"}
                      </p>
                      <p className="mt-2 text-sm text-slate-300">
                        {getWorkspaceLocation(workspace)}
                      </p>
                      <p className="mt-5 text-sm text-slate-200">
                        {workspace.description ||
                          "Professional workspace opportunity listed by a salon owner inside the shared Ellyanna product family."}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-4 bg-white p-6">
                    <div className="flex flex-wrap gap-2">
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
                        {titleCaseLabel(workspace.booking_mode, "Internal")}
                      </span>
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
                        {titleCaseLabel(workspace.marketplace_status, "Published")}
                      </span>
                      {workspace.is_featured ? (
                        <span className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700">
                          Featured
                        </span>
                      ) : null}
                    </div>
                    <div className="space-y-2 text-sm text-slate-600">
                      <div>
                        <span className="font-semibold text-slate-900">Daily:</span>{" "}
                        {workspace.price_per_day
                          ? `${formatWorkspaceMoney(workspace.price_per_day)}/day`
                          : "Available on request"}
                      </div>
                      <div>
                        <span className="font-semibold text-slate-900">Weekly:</span>{" "}
                        {workspace.price_per_week
                          ? `${formatWorkspaceMoney(workspace.price_per_week)}/week`
                          : "Custom with workspace provider"}
                      </div>
                      <div>
                        <span className="font-semibold text-slate-900">Amenities:</span>{" "}
                        {Array.isArray(workspace.amenity_addons) &&
                        workspace.amenity_addons.length
                          ? workspace.amenity_addons
                              .slice(0, 3)
                              .map((addon) => addon?.label || addon?.name || addon?.key)
                              .filter(Boolean)
                              .join(", ")
                          : "Shared salon amenities"}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <Link
                        to={getWorkspaceListingPath(workspace)}
                        className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                      >
                        View Workspace
                      </Link>
                      <Link
                        to={getWorkspaceCheckoutPath(workspace)}
                        className="rounded-full bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600"
                      >
                        Open Checkout
                      </Link>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </MarketplaceShell>
  );
}
